import React from "react";
import { Button } from "reactstrap";
import NavHeader from "./NavHeader";
import NavRow from "./NavRow";
import InfoTorneo from "./infoTorneo";
import { genId, nuevoObjectId } from "./ids";
import { importarEstructura } from "services/torneosService";
import { toastBigError, toastConfirm } from "components/utils/toasts";
import { fechasDeZonas, jugados, partidosDeFechas, textoArrastre } from "./bajas";

// Pantalla raíz del torneo.
// - Torneo nuevo (isSaved=false): solo nombre/año + botón "Guardar Torneo".
// - Torneo guardado (isSaved=true): Equipos + Ruedas; cada cambio auto-persiste,
//   sin botón de guardar.
export default function HomeScreen({
  torneo,
  isSaved,
  onInfoChange,
  onRuedasChange,
  onOpenTeams,
  onOpenRound,
  onOpenEstructura,
  onCreate,
}) {
  const estructuraRef = React.useRef(null);
  const [isImporting, setIsImporting] = React.useState(false);

  const addRound = () => {
    // El _id se genera acá (ver ids.js): las fechas y los partidos que se carguen
    // adentro se guardan de a uno y necesitan poder nombrar su rueda y su zona.
    const nueva = {
      id: genId("round"),
      _id: nuevoObjectId(),
      name: "Rueda " + (torneo.ruedas.length + 1),
      zones: [],
    };
    onRuedasChange([...torneo.ruedas, nueva]);
  };

  // Borrar una rueda se lleva sus zonas, fechas, partidos y resultados: se avisa
  // cuánto es antes de confirmar.
  const removeRound = (rueda) => {
    const fechas = fechasDeZonas(rueda.zones);
    const partidos = partidosDeFechas(fechas);
    const texto =
      textoArrastre({
        zonas: rueda.zones.length,
        fechas: fechas.length,
        partidos: partidos.length,
        resultados: jugados(partidos),
      }) || "La rueda todavía está vacía.";

    toastConfirm("¿ Eliminar " + rueda.name + " ?", texto, () =>
      onRuedasChange(torneo.ruedas.filter((r) => r.id !== rueda.id))
    );
  };

  // El cuadro de cruces del año trae una solapa por zona. El server sólo lo lee y
  // devuelve los nombres de equipo de cada solapa; qué equipo es cada nombre y qué se
  // crea con eso se decide en la pantalla de previsualización.
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsImporting(true);
    let data;
    try {
      data = await importarEstructura(file);
    } finally {
      // Pase lo que pase, el velo de "Importando archivo..." se tiene que ir.
      setIsImporting(false);
    }
    // Permite volver a subir el mismo archivo (si no, el change no se dispara).
    e.target.value = "";
    if (data && data.success) onOpenEstructura(data.records);
    else toastBigError("No se pudo importar", (data && data.message) || "Revisá el archivo e intentá de nuevo.");
  };

  return (
    <div className="screen-slide">
      {isImporting && (
        <div className="loading-overlay">
          <div className="d-flex flex-column justify-content-center align-items-center h-100">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <span className="mt-2">Importando archivo...</span>
          </div>
        </div>
      )}
      <NavHeader
        variant="home"
        title={torneo.nombre || "Nuevo Torneo"}
        subtitle={torneo.ano ? String(torneo.ano) : "Indique año"}
      />

      <InfoTorneo torneo={{ nombre: torneo.nombre, ano: torneo.ano }} onChange={onInfoChange} />

      {!isSaved ? (
        <>
          <p className="text-muted mt-3" style={{ fontSize: "0.875rem" }}>
            Guardá el torneo para empezar a cargar equipos, ruedas, zonas, fechas y partidos.
          </p>
          <Button className="btn-arhock btn-block mt-3 mb-0" onClick={onCreate} style={{ fontWeight: 600 }}>
            Guardar Torneo
          </Button>
        </>
      ) : (
        <>
          <div className="mt-4">
            <NavRow
              title="Equipos"
              badges={[{ label: torneo.equipos.length + " cargados", variant: "arhock" }]}
              onClick={onOpenTeams}
            />
          </div>

          <div className="mt-4">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="section-title mb-0">Ruedas</div>
              <div className="d-flex flex-wrap justify-content-end" style={{ gap: "0.5rem" }}>
                <Button
                  className="btn-arhock btn-sm my-0"
                  onClick={() => estructuraRef.current.click()}
                  disabled={isImporting}
                >
                  {isImporting ? "Leyendo..." : "Importar Estructura"}
                </Button>
                <Button className="btn-arhock btn-sm my-0" onClick={addRound}>
                  + Agregar Rueda
                </Button>
              </div>
            </div>
            <input
              type="file"
              className="d-none"
              ref={estructuraRef}
              accept=".xlsx, .xls"
              onChange={handleFileChange}
            />

            {torneo.ruedas.length === 0 ? (
              <div className="empty-state">
                <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                <p className="mb-1 font-weight-bold">No hay ruedas creadas</p>
                <p className="text-muted" style={{ fontSize: "0.875rem" }}>
                  Agregá una rueda para configurar las zonas, fechas y partidos.
                </p>
              </div>
            ) : (
              torneo.ruedas.map((rueda) => (
                <NavRow
                  key={rueda.id}
                  title={rueda.name}
                  badges={[{ label: rueda.zones.length + " zonas", variant: "teams" }]}
                  onClick={() => onOpenRound(rueda.id)}
                  onDelete={() => removeRound(rueda)}
                />
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
